/**
 * Video timeline cue selection — pure functions, no side effects at load time.
 *
 * Each sticky-steps "timeline caption" has its own display interval [at, until), and **at any moment it is valid for no caption to be shown at all**:
 * in the latter part of the footage, visual content appears on the right side of the video, and the captions must then step aside and disappear (requirement confirmed 2026-08-05).
 *
 * ⚠️ This is NOT the same function as pickCue in the product page's assets/scroll-video-split-captions.js; do not reuse one for the other:
 * that one is "one after another, the next replaces the previous, never left empty"; this one is "each has its own start/end, gaps allowed".
 * This file once held a second implementation of pickCue plus an assertion lock requiring "both implementations agree point by point"; once the requirement
 * changed to start/end intervals the semantics diverged, so that copied implementation and its consistency lock were both removed — the product page's version is untouched.
 */
(function () {
	"use strict";

	// currentTime is often a value like 2.4999999, one floating-point speck (order of 1e-7) below the cut point.
	// EPS makes frame boundaries reliably land in the new segment instead of jittering between two segments.
	// 1e-4 (0.1ms) rather than 1e-3: one frame at 24fps is 41.7ms, and 1ms is already a genuinely distinguishable
	// interval; using 1e-3 would misjudge "1ms before the cut point" as the next segment.
	const EPS = 1e-4;

	/**
	 * Index of the cue that should be shown at time t; returns -1 when t is in no interval (a gap / everything has finished).
	 *
	 * Intervals are [at, until): start inclusive, end exclusive; when two adjacent cues have `A.until == B.at` they hand over seamlessly and never light up together.
	 * Both ends yield by EPS, so floating-point specks in currentTime don't make captions flicker at the boundaries.
	 *
	 * When intervals overlap, the **earliest** one is returned (cues are sorted by at ascending). Overlap means the admin entry is wrong;
	 * we neither throw nor merge here, and only guarantee at most one lit at any moment.
	 *
	 * @param {Array<{at: number, until: number}>} cues sorted by at ascending; a non-finite until means "until the video ends"
	 * @param {number} t seconds
	 * @returns {number} index, or -1
	 */
	function pickWindow(cues, t) {
		for (let i = 0; i < cues.length; i += 1) {
			const cue = cues[i];
			if (t + EPS < cue.at) break; // ascending, so later ones only start later
			const until = Number.isFinite(cue.until) ? cue.until : Infinity;
			if (t + EPS < until) return i;
		}
		return -1;
	}

	const api = { pickWindow, EPS };
	if (typeof module === "object" && module && module.exports) {
		module.exports = api;
	}
	if (typeof globalThis !== "undefined") {
		globalThis.SoulogVideoCues = api;
	}
})();
